import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [residentialDropdown, setResidentialDropdown] = useState(false);
  const [commercialDropdown, setCommercialDropdown] = useState(false);
  const [mobileResidentialOpen, setMobileResidentialOpen] = useState(false);
  const [mobileCommercialOpen, setMobileCommercialOpen] = useState(false);
  const [mobileLocationsOpen, setMobileLocationsOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const closeDropdowns = () => {
    setResidentialDropdown(false);
    setCommercialDropdown(false);
  };

  // Overlay click closes mobile menu
  const handleOverlayClick = (e) => {
    if (e.target.id === 'mobile-menu-overlay') setIsOpen(false);
  };

  return (
    <header className="bg-[#f8f7f3] shadow-md text-[#4a3f35] sticky top-0 z-50">
      {/* Top Row */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-2 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <a href="tel:6027300728" className="text-[#4a3f35] hover:text-[#b87c4c] flex items-center space-x-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
              </svg>
              <span className="font-medium">(602) 730-0728</span>
            </a>
          </div>
          <NavLink 
            to="/contact" 
            className="inline-block px-6 py-2 bg-[#8B4513] text-white font-medium rounded-lg shadow-md hover:bg-[#6B3410] transition-colors duration-200 ease-in-out"
          >
            Get Free Quote
          </NavLink>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center pr-6 md:pr-8 lg:pr-10">
            <NavLink to="/">
            <picture>
              <source srcSet="/Copper Canyon_text logo_lt back 2.avif" type="image/avif" />
              <source srcSet="/Copper Canyon_text logo_lt back 2.webp" type="image/webp" />
            <img 
              src="/Copper Canyon_text logo_lt back 2.png" 
              alt="Copper Canyon Pressure Wash Logo" 
                title="Copper Canyon Pressure Wash - Professional Pressure Washing Services"
              className="h-10 sm:h-14 md:h-16 w-auto object-contain"
                width="300"
                height="64"
                loading="lazy"
            />
            </picture>
            </NavLink>
          </div>

          {/* Hamburger Icon (show below lg) */}
          <button
            className="lg:hidden text-[#4a3f35] focus:outline-none w-10 h-10 flex items-center justify-center"
            onClick={() => setIsOpen(true)}
            aria-label="Open menu"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

          {/* Desktop Nav (show on lg and up) */}
          <div className="hidden lg:block">
            <ul className="flex flex-row space-x-8 items-center">
              <li className="relative group flex items-center">
                <button
                  onClick={() => setResidentialDropdown(!residentialDropdown)}
                  className="text-lg font-medium text-[#4a3f35] hover:text-[#b87c4c] transition-colors duration-200 flex items-center py-2 focus:outline-none"
                  aria-haspopup="true"
                  aria-expanded={residentialDropdown}
                  style={{ paddingRight: 0 }}
                >
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      navigate('/residential');
                      setResidentialDropdown(false);
                    }}
                    className="cursor-pointer pr-1"
                >
                  Residential
                  </span>
                  <svg
                    className={`w-4 h-4 ml-1 transition-transform duration-200 ${residentialDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    onClick={e => {
                      e.stopPropagation();
                      setResidentialDropdown(!residentialDropdown);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div 
                  className={`${residentialDropdown ? 'block' : 'hidden'} sm:hidden sm:group-hover:block absolute left-0 top-full w-64 bg-white rounded-lg shadow-lg z-50`}
                >
                  <div className="py-2 relative before:content-[''] before:absolute before:top-[-10px] before:left-0 before:right-0 before:h-[10px] before:bg-transparent">
                    <NavLink to="/pressure-washing" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Pressure Washing</NavLink>
                    <NavLink to="/driveway-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Driveway Cleaning</NavLink>
                    <NavLink to="/solar-panel-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Solar Panel Cleaning</NavLink>
                    <NavLink to="/pool-deck-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Pool Deck Cleaning</NavLink>
                    <NavLink to="/patio-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Patio Cleaning</NavLink>
                    <NavLink to="/gutter-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Gutter Cleaning</NavLink>
                    <NavLink to="/deck-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Deck Cleaning</NavLink>
                    <NavLink to="/fence-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Fence Cleaning</NavLink>
                  </div>
                </div>
              </li>
              
              {/* Commercial Dropdown */}
              <li className="relative group flex items-center">
                <button
                  onClick={() => setCommercialDropdown(!commercialDropdown)}
                  className="text-lg font-medium text-[#4a3f35] hover:text-[#b87c4c] transition-colors duration-200 flex items-center py-2 focus:outline-none"
                  aria-haspopup="true"
                  aria-expanded={commercialDropdown}
                  style={{ paddingRight: 0 }}
                >
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      navigate('/commercial');
                      setCommercialDropdown(false);
                    }}
                    className="cursor-pointer pr-1"
                >
                  Commercial
                  </span>
                  <svg
                    className={`w-4 h-4 ml-1 transition-transform duration-200 ${commercialDropdown ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    onClick={e => {
                      e.stopPropagation();
                      setCommercialDropdown(!commercialDropdown);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div 
                  className={`${commercialDropdown ? 'block' : 'hidden'} sm:hidden sm:group-hover:block absolute left-0 top-full w-64 bg-white rounded-lg shadow-lg z-50`}
                >
                  <div className="py-2 relative before:content-[''] before:absolute before:top-[-10px] before:left-0 before:right-0 before:h-[10px] before:bg-transparent">
                    <NavLink to="/commercial-concrete-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Commercial Concrete Cleaning</NavLink>
                    <NavLink to="/graffiti-removal" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Graffiti Removal</NavLink>
                    <NavLink to="/window-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Window Cleaning</NavLink>
                    <NavLink to="/sidewalk-cleaning" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Sidewalk Cleaning</NavLink>
                  </div>
                </div>
              </li>
              {/* Locations Dropdown */}
              <li className="relative group flex items-center">
                <button
                  className="text-lg font-medium text-[#4a3f35] hover:text-[#b87c4c] transition-colors duration-200 flex items-center py-2"
                >
                  Locations
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div className="hidden group-hover:block absolute left-0 top-full w-64 bg-white rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    <NavLink to="/phoenix-power-washing" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Phoenix</NavLink>
                    <NavLink to="/scottsdale-power-washing" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Scottsdale</NavLink>
                    <NavLink to="/mesa-power-washing" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Mesa</NavLink>
                    <NavLink to="/tempe-power-washing" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Tempe</NavLink>
                    <NavLink to="/gilbert-power-washing" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Gilbert</NavLink>
                    <NavLink to="/paradise-valley-power-washing" className="block px-4 py-2 text-[#4a3f35] hover:bg-[#f8f7f3] hover:text-[#b87c4c]">Paradise Valley</NavLink>
                  </div>
                </div>
              </li>
              <li className="flex items-center">
                <NavLink 
                  to="/about" 
                  className={({isActive}) => 
                    `text-lg font-medium transition-colors duration-200 py-2 ${
                      isActive 
                        ? 'text-[#b87c4c] font-semibold' 
                        : 'text-[#4a3f35] hover:text-[#b87c4c]'
                    }`
                  }
                >
                  About
                </NavLink>
              </li>
              <li className="flex items-center">
                <NavLink 
                  to="/contact" 
                  className={({isActive}) => 
                    `text-lg font-medium transition-colors duration-200 py-2 ${
                      isActive 
                        ? 'text-[#b87c4c] font-semibold' 
                        : 'text-[#4a3f35] hover:text-[#b87c4c]'
                    }`
                  }
                >
                  Contact
                </NavLink>
              </li>
              <li className="flex items-center">
                <NavLink 
                  to="/blog" 
                  className={({isActive}) => 
                    `text-lg font-medium transition-colors duration-200 py-2 ${
                      isActive 
                        ? 'text-[#b87c4c] font-semibold' 
                        : 'text-[#4a3f35] hover:text-[#b87c4c]'
                    }`
                  }
                >
                  Blog
                </NavLink>
              </li>
              <li className="flex items-center">
                <NavLink 
                  to="/pricing" 
                  className={({isActive}) => 
                    `text-lg font-medium transition-colors duration-200 py-2 ${
                      isActive 
                        ? 'text-[#b87c4c] font-semibold' 
                        : 'text-[#4a3f35] hover:text-[#b87c4c]'
                    }`
                  }
                >
                  Pricing
                </NavLink>
              </li>
            </ul>
          </div>

          {/* Mobile Sliding Menu (show below lg) */}
          {isOpen && (
            <div
              id="mobile-menu-overlay"
              className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-end"
              onClick={handleOverlayClick}
            >
              <div className="w-4/5 max-w-xs h-full bg-white shadow-2xl p-6 flex flex-col relative animate-slide-in-right overflow-y-auto max-h-screen">
                {/* Close button */}
                <button
                  className="absolute top-4 right-4 text-3xl text-[#4a3f35] focus:outline-none"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close menu"
                >
                  &times;
                </button>
                <img
                  src="/Copper Canyon_text logo_lt back 2.png"
                  alt="Copper Canyon Pressure Wash Logo"
                  title="Copper Canyon Pressure Wash - Professional Pressure Washing Services"
                  className="h-10 w-auto object-contain mb-8 mx-auto"
                />
                <ul className="flex flex-col space-y-4">
                  <li>
                    <div className="flex items-center justify-between">
                      <span
                        className="text-lg font-medium text-[#4a3f35] cursor-pointer"
                        onClick={() => {
                          setIsOpen(false);
                          navigate('/residential');
                        }}
                      >
                        Residential
                      </span>
                      <button
                        className="focus:outline-none"
                        onClick={() => setMobileResidentialOpen(open => !open)}
                        aria-expanded={mobileResidentialOpen}
                        aria-controls="mobile-residential-menu"
                      >
                        <svg className={`w-4 h-4 ml-1 transition-transform duration-200 ${mobileResidentialOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                    {mobileResidentialOpen && (
                      <ul id="mobile-residential-menu" className="ml-4 space-y-2">
                        <li><NavLink to="/pressure-washing" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Pressure Washing</NavLink></li>
                      <li><NavLink to="/driveway-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Driveway Cleaning</NavLink></li>
                      <li><NavLink to="/solar-panel-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Solar Panel Cleaning</NavLink></li>
                      <li><NavLink to="/pool-deck-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Pool Deck Cleaning</NavLink></li>
                      <li><NavLink to="/patio-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Patio Cleaning</NavLink></li>
                        <li><NavLink to="/gutter-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Gutter Cleaning</NavLink></li>
                        <li><NavLink to="/deck-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Deck Cleaning</NavLink></li>
                        <li><NavLink to="/fence-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Fence Cleaning</NavLink></li>
                    </ul>
                    )}
                  </li>
                  <li>
                    <div className="flex items-center justify-between">
                      <span
                        className="text-lg font-medium text-[#4a3f35] cursor-pointer"
                        onClick={() => {
                          setIsOpen(false);
                          navigate('/commercial');
                        }}
                      >
                        Commercial
                      </span>
                      <button
                        className="focus:outline-none"
                        onClick={() => setMobileCommercialOpen(open => !open)}
                        aria-expanded={mobileCommercialOpen}
                        aria-controls="mobile-commercial-menu"
                      >
                        <svg className={`w-4 h-4 ml-1 transition-transform duration-200 ${mobileCommercialOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                    {mobileCommercialOpen && (
                      <ul id="mobile-commercial-menu" className="ml-4 space-y-2">
                        <li><NavLink to="/commercial-concrete-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Commercial Concrete Cleaning</NavLink></li>
                      <li><NavLink to="/graffiti-removal" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Graffiti Removal</NavLink></li>
                      <li><NavLink to="/window-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Window Cleaning</NavLink></li>
                        <li><NavLink to="/sidewalk-cleaning" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Sidewalk Cleaning</NavLink></li>
                    </ul>
                    )}
                  </li>
                  <li>
                    <button
                      className="block w-full text-left text-lg font-medium text-[#4a3f35] mb-1 flex items-center justify-between focus:outline-none"
                      onClick={() => setMobileLocationsOpen((open) => !open)}
                      aria-expanded={mobileLocationsOpen}
                      aria-controls="mobile-locations-menu"
                    >
                      Locations
                      <svg className={`w-4 h-4 ml-1 transition-transform duration-200 ${mobileLocationsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {mobileLocationsOpen && (
                      <ul id="mobile-locations-menu" className="ml-4 space-y-2">
                      <li><NavLink to="/phoenix-power-washing" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Phoenix</NavLink></li>
                      <li><NavLink to="/scottsdale-power-washing" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Scottsdale</NavLink></li>
                      <li><NavLink to="/mesa-power-washing" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Mesa</NavLink></li>
                      <li><NavLink to="/tempe-power-washing" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Tempe</NavLink></li>
                      <li><NavLink to="/gilbert-power-washing" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Gilbert</NavLink></li>
                      <li><NavLink to="/paradise-valley-power-washing" className="text-[#4a3f35]" onClick={() => setIsOpen(false)}>Paradise Valley</NavLink></li>
                    </ul>
                    )}
                  </li>
                  <li>
                    <NavLink to="/about" className="text-lg font-medium text-[#4a3f35]" onClick={() => setIsOpen(false)}>About</NavLink>
                  </li>
                  <li>
                    <NavLink to="/contact" className="text-lg font-medium text-[#4a3f35]" onClick={() => setIsOpen(false)}>Contact</NavLink>
                  </li>
                  <li>
                    <NavLink to="/blog" className="text-lg font-medium text-[#4a3f35]" onClick={() => setIsOpen(false)}>Blog</NavLink>
                  </li>
                  <li>
                    <NavLink to="/pricing" className="text-lg font-medium text-[#4a3f35]" onClick={() => setIsOpen(false)}>Pricing</NavLink>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </nav>
      {/* Add slide-in animation */}
      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s cubic-bezier(0.4,0,0.2,1) both;
        }
      `}</style>
    </header>
  );
}

export default Header;